import React, { useState, useEffect } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import config from '@/config';
import { useOverviewContext } from '@context/OverviewContext';
import { useSelectedStudent } from '@context/SelectedStudentContext';
import { useSchool } from '@context/SchoolContext';
import { useLinksContext } from '@/context/LinksContext';
import { handleLogout, openAccountLink } from '@/utils/linksUtils';
import Button from '@components/Button/Button';
import StudentSwitcherModal from '@components/StudentSwitcherModal/StudentSwitcherModal';
import { CardSkeleton } from '@components/Skeletons/Skeletons';
import { useSkeletonLoader } from '@/hooks/useSkeletonLoader/useSkeletonLoader';
import SidebarMenu from './SidebarMenu';
import k12Logo from '@assets/images/k12_logo.svg'
import { Footer } from '../Footer/Footer';

export type SidebarType = 'multi-student-lc' | 'single-student-lc' | 'student';

export interface SidebarProfileProps {
    type: SidebarType; 
    userName: string;
    role: string;
    showSwitchButton: boolean;
    onItemClick?: () => void;
    onSwitchStudent?: () => void;
}

const SidebarProfile: React.FC<SidebarProfileProps> = ({
  userName,
  role,
  showSwitchButton,
  type,
  onItemClick,
  onSwitchStudent
}) => {

const [showModal, setShowModal] = useState(false);
const { students, userRoleVal } = useOverviewContext();

const { selectedStudent } = useSelectedStudent();
const { data: schoolData, error: schoolError } = useSchool();
const showSkeleton = useSkeletonLoader({
  schoolData,
  schoolError,
  selectedStudent,
  retryDelay: 3000,
  initialDelay: 2000,
  maxRetries: 2,
});

const fetchStudents = async () => students ?? [];
const hasMultipleStudents = students && students.length > 1;

const schoolName = selectedStudent?.schoolName || '';
const grade = selectedStudent ? `Grade ${selectedStudent.gradeId}` : '';
const studentName = selectedStudent
    ? `${selectedStudent.firstName} ${selectedStudent.lastName}`
    : '';


const { oktaAuth } = useOktaAuth();
const { myAccountLink } = useLinksContext();
const logoSrc = schoolData?.logo_emblem_uri || '';

  return (
    <>
    <div className="menu-container">
        <div className="profileWrapper">
            <div className="profileTop">
                <div className="userName">{userName} </div>

                <div className="roleLinksRow">
                    <div className="userRole">
                     {role
                        .toLowerCase()
                        .split('_')
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(' ')
                     }    
                    </div>
                    <div className="accountLinks">
                        <a href="#" 
                        onClick={(e) => {
                            e.preventDefault();
                            openAccountLink(myAccountLink);
                        }}>Account</a>
                        <span className="divider" />
                        
                        <a href="#" className="logoutLink"
                        onClick={async (e) => {
                            e.preventDefault();
                            await handleLogout(oktaAuth, config.oidc.postLogoutRedirectUri);
                        }}> Logout </a>
                    </div>
                </div>
            </div>
        </div>
            
            <div className="schoolInfoContainer">
                {showSkeleton ? (
                    <CardSkeleton />
                    ) : (
                        <div className="schoolCard">
                            <div className="logoSchoolCard">
                                
                                <img 
                                    src={logoSrc || k12Logo}
                                    alt={`${schoolData?.abbreviation || 'K12'} Logo`}
                                    className="k12Logo"
                                />
                                
                                <div className="schoolNameGrade">
                                    <p className="schoolName">{schoolName}</p>
                                    {role == 'STUDENT' && (
                                        <p className="grade">{grade}</p>
                                    )}
                                </div>
                            </div>
                            
                            {role === 'LEARNING_COACH' && (
                            <div className="schoolDetails">
                                <div className="studentRow">
                                    <div className="studentInfo">                
                                        <span className="studentName">{studentName}</span>
                                        <span className="grade">{grade}</span>
                                    </div>

                                    {showSwitchButton && hasMultipleStudents &&(
                                    <Button
                                        label="Switch"
                                        size="sm"
                                        variant="fill"
                                        width="fit-content"
                                        color="tertiary"
                                        breakpoint="all"
                                        onClick={() => {
                                            if (onSwitchStudent) {
                                                // handle for mobile/tab
                                                onSwitchStudent?.();
                                                onItemClick?.();
                                            } else {
                                                // handle for large screens
                                                setShowModal(true);
                                            }
                                        }}
                                    />
                                    )}
                                </div>
                            </div>
                            )}
                            {role === 'TEACHER' && showSwitchButton && (
                            <div className="schoolDetails teacher-school-switcher">
                                <Button
                                    label="Switch Schools"
                                    size="sm"
                                    variant="fill"
                                    width="fit-content"
                                    color="tertiary"
                                    breakpoint="all"
                                    onClick={() => {
                                        if (onSwitchStudent) {
                                            onSwitchStudent?.();
                                            onItemClick?.();
                                        } else {
                                            setShowModal(true);
                                        }
                                    }}
                                />
                            </div>
                            )}
                        </div>
                    )}
            </div>
        
        <div className="menuList">
            <SidebarMenu onItemClick={onItemClick} />
        </div>
        <Footer />
    </div>
    
    {showModal && (
        <StudentSwitcherModal
            variant={userRoleVal === 'TEACHER' ? 'school' : 'student'}
            fetchStudents={fetchStudents}
            onClose={() => setShowModal(false)}
            onStudentSelect={() => setShowModal(false)}
        />
    )}
    </>
  );
};

export default SidebarProfile;